import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, X, Send, Save, RotateCcw, Eye } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

const DEFAULT_TEMPLATE =
  "━━━━━━━━━━━━━━━━━━ **QUOTA RESULTS** ━━━━━━━━━━━━━━━━━━\n\n" +
  "**Passed:**\n{passed_list}\n\n" +
  "**Failed:**\n{failed_list}\n\n" +
  "**Exempt:**\n{exempt_list}\n\n" +
  "━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n" +
  "{staff_role_ping}\n\n" +
  "If you have any questions regarding the quota results please contact " +
  "{contact_pings}.";

const SAMPLE = {
  archived_week: "Apr 13 - Apr 19, 2026",
  new_week_range: "Apr 20 - Apr 26, 2026",
  archived_count: 18,
  passed_count: 12,
  failed_count: 4,
  exempt_count: 2,
  passed_list: "• <@1387116592210510009> — 3h 19m\n• <@227845414378930176> — 4h 30m",
  failed_list: "• <@811596805250088990> — 40m\n• <@1234567890> — 0",
  exempt_list: "• <@9876543210>",
};

function renderPreview(tpl, cfg) {
  const roleId = (cfg.staff_role_id || "").trim();
  const contactIds = Array.isArray(cfg.contact_user_ids)
    ? cfg.contact_user_ids.filter((s) => String(s).trim())
    : [];
  const contactPings = contactIds.length
    ? contactIds.map((id) => `<@${id}>`).join(" or ")
    : "an administrator";
  return tpl
    .replaceAll("{archived_week}", SAMPLE.archived_week)
    .replaceAll("{new_week_range}", SAMPLE.new_week_range)
    .replaceAll("{archived_count}", SAMPLE.archived_count)
    .replaceAll("{passed_count}", SAMPLE.passed_count)
    .replaceAll("{failed_count}", SAMPLE.failed_count)
    .replaceAll("{exempt_count}", SAMPLE.exempt_count)
    .replaceAll("{passed_list}", SAMPLE.passed_list)
    .replaceAll("{failed_list}", SAMPLE.failed_list)
    .replaceAll("{exempt_list}", SAMPLE.exempt_list)
    .replaceAll("{staff_role_ping}", roleId ? `<@&${roleId}>` : "")
    .replaceAll("{contact_pings}", contactPings)
    .replaceAll("{punch_channel}", cfg.punch_channel_id || "");
}

const QuotaAnnouncementSettings = ({ isOpen, onClose }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quota-history/announcement-config`);
      setConfig(res.data);
      setDirty(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Failed to load config${status ? ` (${status})` : ""}: ${detail}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const update = (key, val) => {
    setConfig((c) => ({ ...c, [key]: val }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const patch = {
        enabled: !!config.enabled,
        channel_id: (config.channel_id || "").trim(),
        webhook_url: (config.webhook_url || "").trim(),
        message_template: config.message_template || "",
        punch_channel_id: (config.punch_channel_id || "").trim(),
        staff_role_id: (config.staff_role_id || "").trim(),
        loa_channel_id: (config.loa_channel_id || "").trim(),
        contact_user_ids: Array.isArray(config.contact_user_ids)
          ? config.contact_user_ids
          : String(config.contact_user_ids || "")
              .split(/[,\s]+/)
              .map((s) => s.trim())
              .filter(Boolean),
      };
      const res = await axios.put(`${API}/quota-history/announcement-config`, patch);
      setConfig(res.data);
      setDirty(false);
      toast.success("Settings saved");
    } catch (e) {
      const detail = e.response?.data?.detail || "Save failed";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (dirty) {
      toast.error("Save your changes first, then send a test");
      return;
    }
    if (!config?.enabled) {
      toast.error("Enable announcements before sending a test");
      return;
    }
    if (!config?.channel_id && !config?.webhook_url) {
      toast.error("Set a Channel ID or Webhook URL first");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/quota-history/announcement-test`);
      if (res.data?.sent) {
        toast.success(`Test sent via ${res.data.via}`);
      } else {
        toast.error(`Test failed: ${res.data?.reason || "unknown"}`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Test failed";
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const handleReset = () => {
    update("message_template", DEFAULT_TEMPLATE);
    toast.info("Template reset to default — click Save to apply");
  };

  if (!isOpen) return null;

  const previewContent = config
    ? renderPreview(config.message_template || DEFAULT_TEMPLATE, config)
    : "";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="announce-settings-overlay"
      >
        <motion.div
          initial={{ scale: 0.96, y: 8 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.96, y: 8 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-4xl max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-zinc-800 scrollbar-thin"
          data-testid="announce-settings-modal"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0A0A0A] border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-[#00E5FF]" />
              </div>
              <div>
                <h2 className="font-mono text-sm font-bold text-white tracking-widest">QUOTA ANNOUNCEMENT</h2>
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Auto-posts every Monday 12:00 AM EST</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="announce-settings-close-btn"
              className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {loading || !config ? (
            <div className="p-12 text-center font-mono text-xs text-zinc-500">Loading…</div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Enabled toggle */}
              <div className="flex items-center justify-between p-4 bg-zinc-900/40 border border-zinc-800">
                <div>
                  <p className="font-mono text-xs font-semibold text-white uppercase tracking-wider">Announcements</p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">Master switch. Off = no auto-post, ever.</p>
                </div>
                <button
                  onClick={() => update("enabled", !config.enabled)}
                  data-testid="announce-toggle-enabled"
                  className={`relative w-12 h-6 transition-colors ${config.enabled ? "bg-[#00E5FF]" : "bg-zinc-700"}`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 bg-black transition-transform ${
                      config.enabled ? "translate-x-6" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>

              {/* Delivery */}
              <div className="space-y-4">
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Delivery</p>

                <div>
                  <label className="block font-mono text-[10px] text-zinc-400 uppercase tracking-wider mb-1.5">
                    Channel ID <span className="text-zinc-600 normal-case">(bot posts here)</span>
                  </label>
                  <input
                    type="text"
                    value={config.channel_id || ""}
                    onChange={(e) => update("channel_id", e.target.value)}
                    placeholder="e.g. 1179608864220520588"
                    data-testid="announce-channel-id"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] text-zinc-400 uppercase tracking-wider mb-1.5">
                    Webhook URL <span className="text-zinc-600 normal-case">(optional — preferred over channel if set)</span>
                  </label>
                  <input
                    type="text"
                    value={config.webhook_url || ""}
                    onChange={(e) => update("webhook_url", e.target.value)}
                    placeholder="https://discord.com/api/webhooks/..."
                    data-testid="announce-webhook-url"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] text-zinc-400 uppercase tracking-wider mb-1.5">
                    Punch Channel ID <span className="text-zinc-600 normal-case">(used for {"<#...>"} link in template)</span>
                  </label>
                  <input
                    type="text"
                    value={config.punch_channel_id || ""}
                    onChange={(e) => update("punch_channel_id", e.target.value)}
                    placeholder="1179608864220520588"
                    data-testid="announce-punch-channel-id"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] text-zinc-400 uppercase tracking-wider mb-1.5">
                    Staff Role ID <span className="text-zinc-600 normal-case">(pinged at the bottom of the announcement)</span>
                  </label>
                  <input
                    type="text"
                    value={config.staff_role_id || ""}
                    onChange={(e) => update("staff_role_id", e.target.value)}
                    placeholder="e.g. 1234567890123456789"
                    data-testid="announce-staff-role-id"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] text-zinc-400 uppercase tracking-wider mb-1.5">
                    Contact User IDs <span className="text-zinc-600 normal-case">(comma or space separated — pinged in the &ldquo;questions, contact&rdquo; line)</span>
                  </label>
                  <input
                    type="text"
                    value={
                      Array.isArray(config.contact_user_ids)
                        ? config.contact_user_ids.join(", ")
                        : (config.contact_user_ids || "")
                    }
                    onChange={(e) => update("contact_user_ids", e.target.value)}
                    placeholder="811596805250088990, 227845414378930176"
                    data-testid="announce-contact-user-ids"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </div>

                <div>
                  <label className="block font-mono text-[10px] text-zinc-400 uppercase tracking-wider mb-1.5">
                    LOA Review Channel ID <span className="text-zinc-600 normal-case">(where /loa requests get posted for approval)</span>
                  </label>
                  <input
                    type="text"
                    value={config.loa_channel_id || ""}
                    onChange={(e) => update("loa_channel_id", e.target.value)}
                    placeholder="1315008937874948147"
                    data-testid="announce-loa-channel-id"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50"
                  />
                </div>
              </div>

              {/* Template */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">Message Template</label>
                  <button
                    onClick={handleReset}
                    data-testid="announce-reset-template-btn"
                    className="flex items-center gap-1 text-[10px] font-mono text-zinc-500 hover:text-[#00E5FF] transition-colors uppercase tracking-wider"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={config.message_template || ""}
                  onChange={(e) => update("message_template", e.target.value)}
                  rows={10}
                  placeholder={DEFAULT_TEMPLATE}
                  data-testid="announce-template-textarea"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-[#00E5FF]/50 resize-y"
                />
                <p className="font-mono text-[10px] text-zinc-600 mt-1.5">
                  Placeholders:{" "}
                  <code className="text-zinc-400">{"{passed_list}"}</code>{" "}
                  <code className="text-zinc-400">{"{failed_list}"}</code>{" "}
                  <code className="text-zinc-400">{"{exempt_list}"}</code>{" "}
                  <code className="text-zinc-400">{"{staff_role_ping}"}</code>{" "}
                  <code className="text-zinc-400">{"{contact_pings}"}</code>{" "}
                  <code className="text-zinc-400">{"{archived_week}"}</code>{" "}
                  <code className="text-zinc-400">{"{new_week_range}"}</code>{" "}
                  <code className="text-zinc-400">{"{passed_count}"}</code>{" "}
                  <code className="text-zinc-400">{"{failed_count}"}</code>{" "}
                  <code className="text-zinc-400">{"{exempt_count}"}</code>{" "}
                  <code className="text-zinc-400">{"{punch_channel}"}</code>
                </p>
              </div>

              {/* Live preview */}
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Eye className="w-3 h-3 text-zinc-500" />
                  <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">Preview (sample data)</p>
                </div>
                <div
                  data-testid="announce-preview"
                  className="bg-[#313338] border border-zinc-800 px-4 py-3 font-sans text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed"
                >
                  {previewContent}
                </div>
              </div>

              {/* Action bar */}
              <div className="sticky bottom-0 -mx-6 -mb-6 bg-[#0A0A0A] border-t border-zinc-800 px-6 py-4 flex items-center justify-between gap-3">
                <button
                  onClick={handleTest}
                  disabled={testing || dirty || !config.enabled}
                  data-testid="announce-send-test-btn"
                  className="flex items-center gap-2 px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider border border-[#FFD600]/40 text-[#FFD600] hover:bg-[#FFD600]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title={dirty ? "Save changes first" : "Send a test announcement to the configured channel"}
                >
                  <Send className="w-3.5 h-3.5" />
                  {testing ? "Sending…" : "Send Test Announcement"}
                </button>

                <div className="flex items-center gap-2">
                  {dirty && <span className="font-mono text-[10px] text-[#FFD600] uppercase tracking-wider">Unsaved changes</span>}
                  <button
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    data-testid="announce-save-btn"
                    className="flex items-center gap-2 px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {saving ? "Saving…" : "Save Settings"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuotaAnnouncementSettings;
